import * as Sentry from '@sentry/react';

import { FieldSelect } from '@/components/looker/field-select';
import {
  type Filter,
  type SelectedFilters,
} from '@/warranty-v3/entities/filters';
import { useCurrentSystemId } from '@/warranty-v3/hooks/use-current-system-id';
import { useSelectedFilters } from '@/warranty-v3/hooks/use-selected-filters';
import { GetFiltersQuery } from '@/warranty-v3/queries/get-filters';

import { FiltersLoading } from './filters-loading';

export function Filters() {
  const [selectedFilters] = useSelectedFilters();
  const systemId = useCurrentSystemId();
  const filtersQuery = GetFiltersQuery.useQuery({ systemId, selectedFilters });

  if (filtersQuery.isLoading) {
    return <FiltersLoading />;
  }

  if (filtersQuery.isError || !filtersQuery.data) {
    Sentry.captureException(filtersQuery.error);
    // If an error happened, we prefer to hide the filters
    return null;
  }

  const { filters } = filtersQuery.data;

  return (
    <div className="flex flex-row gap-4">
      <Filter
        filter={filters.array}
        filterId="arrayId"
        onChangeClear={[
          'nodeId',
          'containerId',
          'bankId',
          'packId',
          'moduleId',
        ]}
        loading={filtersQuery.isFetching}
      />
      <Filter
        filter={filters.node}
        filterId="nodeId"
        onChangeClear={['containerId', 'bankId', 'packId', 'moduleId']}
        loading={filtersQuery.isFetching}
      />
      <Filter
        filter={filters.container}
        filterId="containerId"
        onChangeClear={['bankId', 'packId', 'moduleId']}
        loading={filtersQuery.isFetching}
      />
      <Filter
        filter={filters.bank}
        filterId="bankId"
        onChangeClear={['packId', 'moduleId']}
        loading={filtersQuery.isFetching}
      />
      <Filter
        filter={filters.pack}
        filterId="packId"
        onChangeClear={['moduleId']}
        loading={filtersQuery.isFetching}
      />
      <Filter
        filter={filters.module}
        filterId="moduleId"
        loading={filtersQuery.isFetching}
      />
    </div>
  );
}

type FilterProps = {
  filter?: Filter;
  filterId: keyof SelectedFilters;
  onChangeClear?: (keyof SelectedFilters)[];
  loading: boolean;
};

function Filter({
  filter,
  filterId,
  onChangeClear = [],
  loading,
}: FilterProps) {
  const [selectedFilters, setSelectedFilters] = useSelectedFilters();

  function handleChange(value: string | undefined) {
    setSelectedFilters(function (state) {
      const newFilters = { ...state };

      if (value) {
        for (const fieldId of onChangeClear) {
          newFilters[fieldId] = undefined;
        }
      }

      newFilters[filterId] = value;

      return newFilters;
    });
  }

  if (!filter) {
    return null;
  }

  return (
    <FieldSelect
      label={filter.label}
      placeholder={`Filter by ${filter.label}`}
      value={selectedFilters[filterId]}
      options={filter.values.map(({ id, label }) => ({
        value: id,
        label: label,
      }))}
      onChange={handleChange}
      isClearable={true}
      isLoading={loading}
      disabled={loading}
    />
  );
}
